// Shared client-side CSV export. Data is already local (PowerSync), so export
// runs entirely in the browser — build the text, hand it to the user as a file.

export interface CsvColumn<T> {
  header: string;
  /** Extract the cell value for a row as a plain string. */
  get: (row: T) => string;
}

/** RFC-4180 field escaping: wrap in quotes and double any embedded quotes. */
function csvField(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

export function buildCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const lines = [
    columns.map((c) => csvField(c.header)).join(','),
    ...rows.map((r) => columns.map((c) => csvField(c.get(r))).join(',')),
  ];
  // Prepend a UTF-8 BOM so Excel reads non-ASCII (₱, accented names) correctly.
  return '﻿' + lines.join('\r\n');
}

/**
 * Trigger a browser download of `rows` as CSV. The file is named
 * `${filenamePrefix}-YYYY-MM-DD.csv` using today's date.
 */
export function downloadCsv<T>(rows: T[], columns: CsvColumn<T>[], filenamePrefix: string): void {
  const blob = new Blob([buildCsv(rows, columns)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
